import React, { useState } from 'react';
import { toast } from 'react-toastify';
import './StatusChangeModal.css';

const STATUS_OPTIONS = [
  { value: 'Pending', label: 'Pending', color: 'pending' },
  { value: 'Processing', label: 'Processing', color: 'processing' },
  { value: 'Completed', label: 'Completed', color: 'completed' },
  { value: 'Cancelled', label: 'Cancelled', color: 'cancelled' }
];

const PAYMENT_STATUS_OPTIONS = [
  { value: 'Pending', label: 'Pending' },
  { value: 'Paid', label: 'Paid' },
  { value: 'Refunded', label: 'Refunded' }
];

const StatusChangeModal = ({ isOpen, onClose, onConfirm, currentStatus, currentPaymentStatus, orderId }) => {
  const [newStatus, setNewStatus] = useState(currentStatus);
  const [newPaymentStatus, setNewPaymentStatus] = useState(currentPaymentStatus);
  const [notes, setNotes] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  if (!isOpen) return null;

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      await onConfirm({
        status: newStatus,
        paymentStatus: newPaymentStatus,
        notes: notes,
        orderId: orderId
      });
      toast.success(`Order status updated to ${newStatus}`, {
        position: 'bottom-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } catch (error) {
      toast.error('Failed to update order status', {
        position: 'bottom-right',
        autoClose: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setNewStatus(currentStatus);
    setNewPaymentStatus(currentPaymentStatus);
    setNotes('');
    onClose();
  };

  const hasChanges = newStatus !== currentStatus || newPaymentStatus !== currentPaymentStatus || notes.trim() !== '';

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="status-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="status-modal-header">
          <h2>Change Order Status</h2>
          <button className="close-btn" onClick={handleClose}>×</button>
        </div>

        <div className="status-modal-body">
          {/* Order ID */}
          <div className="form-group">
            <label className="form-label">Order ID</label>
            <div className="order-id-display">{orderId}</div>
          </div>

          {/* Order Status */}
          <div className="form-group">
            <label className="form-label">Order Status</label>
            <div className="status-options">
              {STATUS_OPTIONS.map(option => (
                <button
                  key={option.value}
                  className={`status-option ${option.color} ${newStatus === option.value ? 'selected' : ''}`}
                  onClick={() => setNewStatus(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Payment Status */}
          <div className="form-group">
            <label className="form-label">Payment Status</label>
            <div className="payment-options">
              {PAYMENT_STATUS_OPTIONS.map(option => (
                <button
                  key={option.value}
                  className={`payment-option ${newPaymentStatus === option.value ? 'selected' : ''}`}
                  onClick={() => setNewPaymentStatus(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Status Change Notes */}
          <div className="form-group">
            <label className="form-label">Change Notes (Optional)</label>
            <textarea
              className="form-textarea"
              placeholder="Add notes about this status change..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>

          {/* Info Message */}
          <div className="info-message">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="16" x2="12" y2="12"></line>
              <line x1="12" y1="8" x2="12.01" y2="8"></line>
            </svg>
            <span>Status changes will be recorded in the order history</span>
          </div>
        </div>

        <div className="status-modal-actions">
          <button className="btn-cancel" onClick={handleClose}>
            Cancel
          </button>
          <button 
            className="btn-save" 
            onClick={handleConfirm}
            disabled={!hasChanges || isLoading}
          >
            {isLoading ? '⏳ Updating...' : '✓ Update Status'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StatusChangeModal;
